import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { PanelLeft } from 'lucide-react';
import * as React from 'react';

import { Button } from '@components/ui/button';
import { Input } from '@components/ui/input';
import { Separator } from '@components/ui/separator';
import { Skeleton } from '@components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { useIsMobile } from '@hooks/mobile.hook';
import { cn } from '@lib/utils';

export const SIDEBAR_COOKIE_NAME = 'sidebar:state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_ICON = '3rem';

type SidebarContext = {
  isMobile: boolean;
  open: boolean;
  setOpen: (open: boolean | ((open: boolean) => boolean)) => void;
  state: 'expanded' | 'collapsed';
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

type SidebarProviderProps = React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
};

const SidebarProvider = (props: SidebarProviderProps) => {
  const {
    children,
    className,
    defaultOpen = true,
    onOpenChange: setOpenProp,
    open: openProp,
    style,
    ...otherProps
  } = props;

  const isMobile = useIsMobile();

  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;

  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const requestedOpenState = typeof value === 'function' ? value(open) : value;
      const openState = isMobile ? false : requestedOpenState;

      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [isMobile, open, setOpenProp],
  );

  React.useEffect(() => {
    if (isMobile && open) {
      setOpen(false);
    }
  }, [isMobile, open, setOpen]);

  const toggleSidebar = React.useCallback(() => {
    setOpen((openValue) => !openValue);
  }, [setOpen]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? 'expanded' : 'collapsed';

  const contextValue = React.useMemo<SidebarContext>(
    () => ({
      isMobile,
      open,
      setOpen,
      state,
      toggleSidebar,
    }),
    [isMobile, open, setOpen, state, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          className={cn(
            'group/sidebar-wrapper flex min-h-[calc(100svh-var(--impersonation-banner-height,0px))] w-full has-data-[variant=inset]:bg-sidebar',
            className,
          )}
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          {...otherProps}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
};
SidebarProvider.displayName = 'SidebarProvider';

type SidebarProps = React.ComponentProps<'div'> & {
  collapsible?: 'offcanvas' | 'icon' | 'none';
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
};

const Sidebar = (props: SidebarProps) => {
  const {
    children,
    className,
    collapsible = 'offcanvas',
    side = 'left',
    style,
    variant = 'sidebar',
    ...otherProps
  } = props;
  const { state } = useSidebar();

  if (collapsible === 'none') {
    return (
      <div
        className={cn(
          'flex h-full w-(--sidebar-width) flex-col bg-linear-to-br from-sidebar-gradient-from to-sidebar-gradient-to text-sidebar-foreground',
          className,
        )}
        style={style}
        {...otherProps}
      >
        {children}
      </div>
    );
  }

  const sidebarStyle: React.CSSProperties = {
    top: 'var(--impersonation-banner-height, 0px)',
    height: 'calc(100svh - var(--impersonation-banner-height, 0px))',
    ...style,
  };

  return (
    <div
      className="group peer block text-sidebar-foreground"
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-side={side}
      data-state={state}
      data-variant={variant}
    >
      <div
        className={cn(
          'relative h-[calc(100svh-var(--impersonation-banner-height,0px))] w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
        )}
      />
      <div
        className={cn(
          'fixed z-10 flex w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear',
          side === 'left'
            ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          variant === 'floating' || variant === 'inset'
            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=right]:border-l',
          className,
        )}
        style={sidebarStyle}
        {...otherProps}
      >
        <div
          className="flex h-full w-full flex-col bg-linear-to-br from-sidebar-gradient-from to-sidebar-gradient-to group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
          data-sidebar="sidebar"
        >
          {children}
        </div>
      </div>
    </div>
  );
};

Sidebar.displayName = 'Sidebar';

type SidebarTriggerProps = React.ComponentProps<typeof Button> & {
  tooltip?: React.ComponentProps<typeof TooltipContent> | string;
};

const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>((props, ref) => {
  const { className, onClick, tooltip, ...otherProps } = props;
  const { isMobile, state, toggleSidebar } = useSidebar();

  const button = (
    <Button
      className={cn('h-7 w-7', className)}
      data-sidebar="trigger"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      size="icon"
      variant="ghost"
      ref={ref}
      {...otherProps}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );

  const shouldShowTooltip = Boolean(tooltip) && state === 'collapsed' && !isMobile;
  if (!shouldShowTooltip) {
    return button;
  }

  const tooltipProps = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        align="center"
        className="bg-sidebar border-sidebar-border text-sidebar-foreground"
        side="right"
        {...tooltipProps}
      />
    </Tooltip>
  );
});
SidebarTrigger.displayName = 'SidebarTrigger';

const SidebarRail = (props: React.ComponentProps<'button'>) => {
  const { className, ...otherProps } = props;
  const { toggleSidebar } = useSidebar();

  return (
    <button
      aria-label="Toggle Sidebar"
      className={cn(
        'absolute inset-y-0 z-20 hidden w-4 min-w-4 max-w-4 -translate-x-1/2 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex',
        'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      data-sidebar="rail"
      onClick={toggleSidebar}
      tabIndex={-1}
      title="Toggle Sidebar"
      {...otherProps}
    />
  );
};
SidebarRail.displayName = 'SidebarRail';

const SidebarInset = (props: React.ComponentProps<'main'>) => {
  const { className, ...otherProps } = props;

  return (
    <main
      className={cn(
        'relative flex min-h-[calc(100svh-var(--impersonation-banner-height,0px))] w-full flex-1 flex-col bg-background',
        'peer-data-[variant=inset]:min-h-[calc(100svh-var(--impersonation-banner-height,0px)-(--spacing(4)))] md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow',
        className,
      )}
      {...otherProps}
    />
  );
};
SidebarInset.displayName = 'SidebarInset';

const SidebarInput = (props: React.ComponentProps<typeof Input>) => {
  const { className, ...otherProps } = props;

  return (
    <Input
      className={cn(
        'h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        className,
      )}
      data-sidebar="input"
      {...otherProps}
    />
  );
};
SidebarInput.displayName = 'SidebarInput';

const SidebarHeader = (props: React.ComponentProps<'div'>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className={cn('flex flex-col gap-2 p-2', className)}
      data-sidebar="header"
      {...otherProps}
    />
  );
};
SidebarHeader.displayName = 'SidebarHeader';

const SidebarFooter = (props: React.ComponentProps<'div'>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className={cn('flex flex-col gap-2 p-2', className)}
      data-sidebar="footer"
      {...otherProps}
    />
  );
};
SidebarFooter.displayName = 'SidebarFooter';

const SidebarSeparator = (props: React.ComponentProps<typeof Separator>) => {
  const { className, ...otherProps } = props;

  return (
    <Separator
      className={cn('mx-2 w-auto bg-sidebar-border', className)}
      data-sidebar="separator"
      {...otherProps}
    />
  );
};
SidebarSeparator.displayName = 'SidebarSeparator';

const SidebarContent = (props: React.ComponentProps<'div'>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden group-data-[collapsible=icon]:overflow-visible',
        className,
      )}
      data-sidebar="content"
      {...otherProps}
    />
  );
};
SidebarContent.displayName = 'SidebarContent';

const SidebarGroup = (props: React.ComponentProps<'div'>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      data-sidebar="group"
      {...otherProps}
    />
  );
};
SidebarGroup.displayName = 'SidebarGroup';

type SidebarGroupLabelProps = React.ComponentProps<'div'> & {
  asChild?: boolean;
};

const SidebarGroupLabel = React.forwardRef<HTMLDivElement, SidebarGroupLabelProps>((props, ref) => {
  const { asChild = false, className, ...otherProps } = props;
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      className={cn(
        'duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opacity] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
        className,
      )}
      data-sidebar="group-label"
      ref={ref}
      {...otherProps}
    />
  );
});
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

type SidebarGroupActionProps = React.ComponentProps<'button'> & {
  asChild?: boolean;
};

const SidebarGroupAction = React.forwardRef<HTMLButtonElement, SidebarGroupActionProps>((props, ref) => {
  const { asChild = false, className, ...otherProps } = props;
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      className={cn(
        'absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'after:absolute after:-inset-2 after:md:hidden',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      data-sidebar="group-action"
      ref={ref}
      {...otherProps}
    />
  );
});
SidebarGroupAction.displayName = 'SidebarGroupAction';

const SidebarGroupContent = (props: React.ComponentProps<'div'>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className={cn('w-full text-sm', className)}
      data-sidebar="group-content"
      {...otherProps}
    />
  );
};
SidebarGroupContent.displayName = 'SidebarGroupContent';

const SidebarMenu = (props: React.ComponentProps<'ul'>) => {
  const { className, ...otherProps } = props;

  return (
    <ul
      className={cn('flex w-full min-w-0 flex-col gap-1', className)}
      data-sidebar="menu"
      {...otherProps}
    />
  );
};
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = (props: React.ComponentProps<'li'>) => {
  const { className, ...otherProps } = props;

  return (
    <li
      className={cn('group/menu-item relative', className)}
      data-sidebar="menu-item"
      {...otherProps}
    />
  );
};
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none ring-sidebar-ring transition-[height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! group-data-[collapsible=icon]:px-1.5 group-data-[collapsible=icon]:justify-center [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      size: {
        default: 'h-8 text-sm',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
        sm: 'h-7 text-xs',
      },
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline:
          'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
    },
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
  },
);

type SidebarMenuButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof sidebarMenuButtonVariants> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: React.ComponentProps<typeof TooltipContent> | string;
  };

const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>((props, ref) => {
  const {
    asChild = false,
    className,
    isActive = false,
    size = 'default',
    tooltip,
    variant = 'default',
    ...otherProps
  } = props;
  const Comp = asChild ? Slot : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      className={cn(sidebarMenuButtonVariants({ size, variant }), className)}
      data-active={isActive}
      data-sidebar="menu-button"
      data-size={size}
      ref={ref}
      {...otherProps}
    />
  );

  const shouldShowTooltip = Boolean(tooltip) && state === 'collapsed' && !isMobile;
  if (!shouldShowTooltip) {
    return button;
  }

  const tooltipProps = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        align="center"
        className="bg-sidebar border-sidebar-border text-sidebar-foreground"
        side="right"
        {...tooltipProps}
      />
    </Tooltip>
  );
});
SidebarMenuButton.displayName = 'SidebarMenuButton';

type SidebarMenuActionProps = React.ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
};

const SidebarMenuAction = React.forwardRef<HTMLButtonElement, SidebarMenuActionProps>((props, ref) => {
  const { asChild = false, className, showOnHover = false, ...otherProps } = props;
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      className={cn(
        'absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0',
        'after:absolute after:-inset-2 after:md:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0',
        className,
      )}
      data-sidebar="menu-action"
      ref={ref}
      {...otherProps}
    />
  );
});
SidebarMenuAction.displayName = 'SidebarMenuAction';

const SidebarMenuBadge = (props: React.ComponentProps<'div'>) => {
  const { className, ...otherProps } = props;

  return (
    <div
      className={cn(
        'absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none',
        'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      data-sidebar="menu-badge"
      {...otherProps}
    />
  );
};
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

type SidebarMenuSkeletonProps = React.ComponentProps<'div'> & {
  showIcon?: boolean;
};

const SidebarMenuSkeleton = (props: SidebarMenuSkeletonProps) => {
  const { className, showIcon = false, ...otherProps } = props;
  const width = React.useMemo(() => `${Math.floor(Math.random() * 40) + 50}%`, []);

  return (
    <div
      className={cn('rounded-md h-8 flex gap-2 px-2 items-center', className)}
      data-sidebar="menu-skeleton"
      {...otherProps}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 flex-1 max-w-(--skeleton-width)"
        data-sidebar="menu-skeleton-text"
        style={{ '--skeleton-width': width } as React.CSSProperties}
      />
    </div>
  );
};
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

const SidebarMenuSub = (props: React.ComponentProps<'ul'>) => {
  const { className, ...otherProps } = props;

  return (
    <ul
      className={cn(
        'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      data-sidebar="menu-sub"
      {...otherProps}
    />
  );
};
SidebarMenuSub.displayName = 'SidebarMenuSub';

const SidebarMenuSubItem = (props: React.ComponentProps<'li'>) => {
  return <li {...props} />;
};
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

type SidebarMenuSubButtonProps = React.ComponentProps<'a'> & {
  asChild?: boolean;
  isActive?: boolean;
  size?: 'sm' | 'md';
  tooltip?: React.ComponentProps<typeof TooltipContent> | string;
};

const SidebarMenuSubButton = React.forwardRef<HTMLAnchorElement, SidebarMenuSubButtonProps>((props, ref) => {
  const { asChild = false, className, isActive, size = 'md', tooltip, ...otherProps } = props;
  const Comp = asChild ? Slot : 'a';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      className={cn(
        'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground',
        'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        size === 'sm' && 'text-xs',
        size === 'md' && 'text-sm',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      data-active={isActive}
      data-sidebar="menu-sub-button"
      data-size={size}
      ref={ref}
      {...otherProps}
    />
  );

  const shouldShowTooltip = Boolean(tooltip) && state === 'collapsed' && !isMobile;
  if (!shouldShowTooltip) {
    return button;
  }

  const tooltipProps = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        align="center"
        className="bg-sidebar border-sidebar-border text-sidebar-foreground"
        side="right"
        {...tooltipProps}
      />
    </Tooltip>
  );
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
